import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import dayjs from 'dayjs';
import { AttendanceApi, StaffAttendanceDailyRow } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useDefaultYear } from './useDefaultYear';
import { formatDate } from '@/lib/format';
import { StaffTypeBadge } from './staffTypeBadge';

const columnHelper = createColumnHelper<StaffAttendanceDailyRow>();

const BASE_COLUMNS = [
  columnHelper.accessor('date', { header: 'Date', cell: (ctx) => formatDate(ctx.getValue()) }),
  columnHelper.accessor('personName', { header: 'Name' }),
  columnHelper.accessor('typeLabel', { header: 'Type', cell: (ctx) => <StaffTypeBadge type={ctx.row.original.type} label={ctx.getValue()} /> }),
  columnHelper.accessor('time', { header: 'Time', cell: (ctx) => ctx.getValue() ?? '—' }),
  columnHelper.accessor('remarks', { header: 'Remarks', cell: (ctx) => ctx.getValue() ?? '—' }),
];

/** Flat, filterable (teacher, date, type) listing across a range — every real attendance event. Reuses StaffAttendanceService.dailyList(schemaName, 'teacher', ...). */
export function TeacherAttendanceReportPage() {
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'date', defaultSortOrder: 'desc' });
  const { selectedYear, isLoading: yearLoading, hasAnyData } = useDefaultYear();

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    ...BASE_COLUMNS,
  ];

  // Default to the current month-to-date, not the whole academic year's
  // full range — same fix as StudentAttendanceReportPage.tsx/
  // AttendanceSummaryPage.tsx.
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    if (!selectedYear) return;
    const yearStart = selectedYear.startDate.slice(0, 10);
    const yearEnd = selectedYear.endDate.slice(0, 10);
    const monthStart = dayjs().startOf('month').format('YYYY-MM-DD');
    const today = dayjs().format('YYYY-MM-DD');
    if (monthStart >= yearStart && today <= yearEnd) {
      setStartDate(monthStart);
      setEndDate(today);
    } else {
      setStartDate(yearStart);
      setEndDate(yearEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'teachers-daily', dt.params, startDate, endDate],
    queryFn: () => AttendanceApi.teachersDaily({ ...dt.params, startDate: startDate || undefined, endDate: endDate || undefined }),
    enabled: !yearLoading && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title="Teacher Attendance Report" description="Every recorded teacher attendance event over a date range." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name"
            className="pl-9"
          />
        </div>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {!hasAnyData && !yearLoading ? (
        <div className="card p-8 text-center text-slate-500">No attendance recorded for the selected filters.</div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for the selected filters."
        />
      )}
    </>
  );
}
